/**
 * The basemap. The ONLY place a map style or a tile source is defined.
 *
 * Esri World Imagery under Esri's boundaries-and-places reference labels, the
 * combination that reads like a satellite hybrid map. No API key. Every data
 * layer a page adds (flood rasters, rivers, extents, boundaries) sits above the
 * labels; markers are DOM and sit above everything.
 *
 * Offline fallback: when the imagery tiles fail, and the page is showing a run,
 * the basemap becomes a grey hillshade of that run's own DEM
 * (GET /runs/{id}/terrain/hillshade). There is no other fallback image.
 *
 * Framing: the map fits `bounds` when it mounts and again whenever `bounds`
 * changes - a new site, scenario or run re-frames the map on that selection's
 * own grid. A time step or a layer toggle does not change `bounds`.
 */

import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Map, { type MapProps, type MapRef } from 'react-map-gl/maplibre'
import type { LayerSpecification, SourceSpecification, StyleSpecification } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'

import { api } from '../api/client'
import { C } from '../design/tokens'
import { MapLibreCorner } from './MapCorner'

export type Bounds = [number, number, number, number]

export const ESRI_ATTRIBUTION = 'Imagery © Esri, Maxar, Earthstar Geographics, and the GIS User Community'

const IMAGERY = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
const LABELS =
  'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}'

/** Tile errors from the imagery before the basemap swaps to the run's own hillshade. */
const FALLBACK_AFTER_ERRORS = 3
const EASE = (t: number) => 1 - (1 - t) ** 3

interface Hillshade {
  png: string
  coordinates: [[number, number], [number, number], [number, number], [number, number]]
}

/** The style: imagery, labels, then the page's own data sources and layers. */
export function basemapStyle(
  extra: { sources?: Record<string, SourceSpecification>; layers?: LayerSpecification[] } = {},
  fallback: Hillshade | null = null,
): StyleSpecification {
  const ground: Record<string, SourceSpecification> = fallback
    ? { ground: { type: 'image', url: fallback.png, coordinates: fallback.coordinates } }
    : {
        satellite: { type: 'raster', tiles: [IMAGERY], tileSize: 256, maxzoom: 19 },
        labels: { type: 'raster', tiles: [LABELS], tileSize: 256, maxzoom: 19 },
      }
  const groundLayers: LayerSpecification[] = fallback
    ? [{ id: 'ground', type: 'raster', source: 'ground', paint: { 'raster-fade-duration': 0 } }]
    : [
        { id: 'satellite', type: 'raster', source: 'satellite', paint: { 'raster-fade-duration': 0 } },
        { id: 'labels', type: 'raster', source: 'labels', paint: { 'raster-opacity': 0.85, 'raster-fade-duration': 0 } },
      ]
  return {
    version: 8,
    sources: { ...ground, ...(extra.sources ?? {}) },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': C.bg } },
      ...groundLayers,
      ...(extra.layers ?? []),
    ],
  }
}

/** A cancelled request (a superseded frame) is not a failure. */
export function isAbort(event: { error?: unknown }): boolean {
  return (event.error as { name?: string } | undefined)?.name === 'AbortError'
}

export interface BaseMapProps extends Omit<MapProps, 'mapStyle' | 'initialViewState' | 'children' | 'padding' | 'attributionControl'> {
  /** Fitted on mount and whenever it changes. Null waits. */
  bounds: Bounds | null
  /** The run whose DEM hillshade stands in when imagery tiles fail. */
  runId?: string | null
  sources?: Record<string, SourceSpecification>
  layers?: LayerSpecification[]
  padding?: number
  /** A saved camera to open on instead of fitting `bounds` the first time. */
  initialView?: { longitude: number; latitude: number; zoom: number } | null
  className?: string
  children?: React.ReactNode
  /** Rendered over the map box, outside the map canvas. */
  overlay?: React.ReactNode
  /** A second attribution the page's own data needs (e.g. OpenStreetMap), shown after the imagery's. */
  attribution?: string
}

export const BaseMap = forwardRef<MapRef | null, BaseMapProps>(function BaseMap(
  { bounds, runId = null, sources, layers, padding = 48, initialView = null, className, children, overlay, attribution, onError, onLoad, ...mapProps },
  forwarded,
) {
  const mapRef = useRef<MapRef | null>(null)
  // A callback ref: react-map-gl creates the map after mount, so the caller's ref
  // must follow the instance, not a value captured on the first render.
  const setRef = useCallback(
    (instance: MapRef | null) => {
      mapRef.current = instance
      if (typeof forwarded === 'function') forwarded(instance)
      else if (forwarded) forwarded.current = instance
    },
    [forwarded],
  )
  const [fallback, setFallback] = useState<Hillshade | null>(null)
  const errors = useRef(0)
  const loaded = useRef(false)

  const style = useMemo(() => basemapStyle({ sources, layers }, fallback), [sources, layers, fallback])
  const key = bounds ? bounds.map((v) => v.toFixed(5)).join(',') : ''

  const fit = useCallback(
    (duration: number) => {
      if (!bounds || !mapRef.current) return
      const [w, s, e, n] = bounds
      mapRef.current.fitBounds(
        [
          [w, s],
          [e, n],
        ],
        { padding, duration, easing: EASE },
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, padding],
  )

  // Every change of selection re-frames on that selection's own bounds.
  const fittedKey = useRef<string | null>(initialView ? key : null)
  useEffect(() => {
    if (!loaded.current || !key || fittedKey.current === key) return
    fittedKey.current = key
    fit(240)
  }, [key, fit])

  const handleError = useCallback(
    (event: Parameters<NonNullable<MapProps['onError']>>[0]) => {
      if (isAbort(event)) return
      const sourceId = (event as unknown as { sourceId?: string }).sourceId
      if (sourceId === 'satellite' || sourceId === 'labels') {
        errors.current += 1
        if (errors.current === FALLBACK_AFTER_ERRORS && runId && !fallback) {
          void api
            .raw<Hillshade>(`/runs/${encodeURIComponent(runId)}/terrain/hillshade`)
            .then(setFallback)
            .catch(() => undefined)
        }
        return
      }
      if (onError) onError(event)
      else console.error(event.error)
    },
    [runId, fallback, onError],
  )

  if (!bounds && !initialView) return <div className={`skeleton h-full w-full ${className ?? ''}`} />
  const [w, s, e, n] = bounds ?? [0, 0, 0, 0]

  return (
    <div className={`relative h-full w-full overflow-hidden ${className ?? ''}`} style={{ background: C.bg }}>
      <Map
        {...(mapProps as Record<string, unknown>)}
        ref={setRef}
        mapStyle={style}
        initialViewState={
          initialView ?? {
            bounds: [
              [w, s],
              [e, n],
            ],
            fitBoundsOptions: { padding },
          }
        }
        onLoad={(event) => {
          loaded.current = true
          if (fittedKey.current !== key) {
            fittedKey.current = key
            fit(0)
          }
          onLoad?.(event)
        }}
        onError={handleError}
        attributionControl={false}
        renderWorldCopies={false}
        style={{ width: '100%', height: '100%' }}
      >
        <MapLibreCorner attribution={[fallback ? 'Hillshade of the run DEM' : ESRI_ATTRIBUTION, attribution].filter(Boolean).join(' · ')} />
        {children}
      </Map>
      {overlay}
    </div>
  )
})
